import { apiFetch } from "./apiClient";

/** One branch's connection to the Aiosell channel manager. The Aiosell partner
 * id and login are server settings and never come through here. */
export interface ChannelManagerConfigDto {
  channelManagerConfigId: number;
  accountId: number;
  branchId: number;
  branchName: string | null;
  provider: string;
  hotelCode: string;
  isEnabled: boolean;
  createdOn: string;
  modifiedOn: string | null;
}

export interface CreateChannelManagerConfigPayload {
  accountId: number;
  branchId: number;
  hotelCode: string;
  isEnabled: boolean;
  createdBy: number | null;
}

export interface UpdateChannelManagerConfigPayload {
  hotelCode: string;
  isEnabled: boolean;
  modifiedBy: number | null;
}

/** Aiosell's answer, with its own message ("Authentication Required!"). */
export interface ChannelManagerCallResult {
  success: boolean;
  message: string | null;
}

export interface AiosellRatePlan {
  ratePlanCode: string;
  ratePlanName: string | null;
  /** Guests the code sells for: 1 for executive-s-ep, 2 for executive-d-ep. */
  occupancy: number | null;
  mealsCount: number | null;
  extraAdultRate: number | null;
}

export interface AiosellRoom {
  roomCode: string;
  roomName: string | null;
  count: number | null;
  active: boolean;
  minOccupancy: number | null;
  maxOccupancy: number | null;
  ratePlans: AiosellRatePlan[];
}

export interface AiosellProperty {
  hotelCode: string;
  hotelName: string | null;
  currency: string | null;
  rooms: AiosellRoom[];
}

export interface AiosellPropertyResult {
  success: boolean;
  message: string | null;
  property: AiosellProperty | null;
}

export interface OtaRoomTypeMap {
  roomTypeId: number;
  roomTypeName?: string | null;
  externalRoomCode: string;
}

export interface OtaRatePlanMap {
  ratePlanId: number;
  ratePlanName?: string | null;
  roomTypeId?: number;
  externalRatePlanCode: string;
  occupancy: number | null;
}

export interface OtaMappings {
  roomTypes: OtaRoomTypeMap[];
  ratePlans: OtaRatePlanMap[];
}

/** One sync run: what went to Aiosell, what was skipped and why. */
export interface OtaSyncSummary {
  success: boolean;
  message: string;
  nightsSent: number;
  inventoryBlocksSent: number;
  rateCodesSent: number;
  skipped: string[];
  errors: string[];
}

/** One run of the check against what Aiosell actually holds. */
export interface OtaCheckSummary {
  success: boolean;
  message: string;
  nightsResent: number;
  /** Aiosell lower than us where an OTA booking still needs attention. */
  nightsHeld: number;
  rateCodesResent: number;
  bookingsImported: number;
  /** Missed bookings listed but not imported (importing switched off). */
  bookingsNotImported: number;
  cancellationsApplied: number;
  bookingsFlagged: number;
  /** Plain lines: what differed and what was done about it. */
  details: string[];
  errors: string[];
}

/** OTA bookings waiting for the front desk, for the menu badge. */
export interface OtaAttention {
  count: number;
}

/** One request sent to Aiosell, as recorded. */
export interface OtaSyncLogEntry {
  otaSyncLogId: number;
  /** "INVENTORY" | "RATES" | "CHECK" | "NOSHOW" */
  kind: string;
  /** "AUTO" | "MANUAL" | "FULL" | "CHECK" */
  source: string;
  success: boolean;
  message: string | null;
  itemCount: number;
  startDate: string | null;
  endDate: string | null;
  requestBody: string | null;
  responseBody: string | null;
  createdOn: string;
}

export interface OtaInventoryPreviewRow {
  roomTypeId: number;
  roomTypeName: string | null;
  roomCode: string;
  sellableRooms: number;
  /** Rooms free per night from `from` on, as the sync counts them. */
  available: number[];
  /** What Aiosell was last told for the same nights; null = never sent. */
  lastSent: (number | null)[];
}

export interface OtaInventoryPreview {
  /** First night, YYYY-MM-DD, in the branch's time zone. */
  from: string;
  timeZone: string;
  rows: OtaInventoryPreviewRow[];
}

/** A booking that arrived from an OTA through Aiosell. */
export interface OtaReservation {
  otaReservationId: number;
  /** The direct booking it became; null when no room could be given. */
  directBookingId: number | null;
  channel: string;
  bookingId: string;
  cmBookingId: string | null;
  checkIn: string;
  checkOut: string;
  /** "BOOKED" | "NEEDS_ATTENTION" | "CANCELLED" | "NO_SHOW" | "IGNORED" */
  status: string;
  attentionReason: string | null;
  /** True: pay at hotel. False: prepaid to the OTA. */
  pah: boolean;
  currency: string | null;
  amountAfterTax: number;
  tax: number;
  commission: number | null;
  guestName: string | null;
  lastAction: string;
  roomNos: string[];
  createdOn: string;
  modifiedOn: string | null;
}

/** One call to the booking webhook, as received. */
export interface OtaWebhookLogEntry {
  otaWebhookLogId: number;
  otaReservationId: number | null;
  /** "WEBHOOK" (from Aiosell) | "TEST" | "CHECK" (found by the check) */
  source: string;
  action: string | null;
  channel: string | null;
  bookingId: string | null;
  httpStatus: number;
  success: boolean;
  message: string | null;
  payload: string | null;
  receivedOn: string;
}

export interface OtaWebhookInfo {
  /** Path on this API; the public address goes in front. */
  path: string;
  credentialsConfigured: boolean;
  username: string | null;
}

export interface OtaWebhookResult {
  httpStatus: number;
  success: boolean;
  message: string;
  otaReservationId: number | null;
  directBookingId: number | null;
}

const base = "/channel-manager/configs";

export const getWebhookInfo = async (): Promise<OtaWebhookInfo> => {
  const res = await apiFetch("/channel-manager/webhook-info");
  return res.json();
};

export const listOtaReservations = async (
  configId: number,
  take = 50
): Promise<OtaReservation[]> => {
  const res = await apiFetch(`${base}/${configId}/reservations?take=${take}`);
  return res.json();
};

export const getWebhookLog = async (
  configId: number,
  take = 30
): Promise<OtaWebhookLogEntry[]> => {
  const res = await apiFetch(`${base}/${configId}/webhook-log?take=${take}`);
  return res.json();
};

/** Aiosell's channel id for Mark No Show, or null when Aiosell cannot pass
 * a no-show to that OTA. Mirrors OtaCheck.NoShowChannel on the server. */
export const noShowChannel = (channel: string): string | null => {
  const key = channel.trim().toLowerCase().replace(/ /g, "");
  if (key === "booking.com" || key === "bookingcom") return "booking.com";
  if (["goibibo", "makemytrip", "mmt", "gommt"].includes(key)) return "gommt";
  return null;
};

/** Tells Aiosell the guest never came, then cancels the stay here. A refusal
 * (by us or by Aiosell) comes back as success: false with the reason. */
export const markOtaNoShow = async (
  configId: number,
  otaReservationId: number
): Promise<ChannelManagerCallResult> => {
  const res = await apiFetch(
    `${base}/${configId}/reservations/${otaReservationId}/no-show`,
    { method: "POST" }
  );
  return res.json();
};

/** Mark resolved: the front desk has dealt with a booking that needed
 * attention. A refusal comes back as success: false with the reason. */
export const resolveOtaReservation = async (
  configId: number,
  otaReservationId: number
): Promise<ChannelManagerCallResult> => {
  const res = await apiFetch(
    `${base}/${configId}/reservations/${otaReservationId}/resolve`,
    { method: "POST" }
  );
  return res.json();
};

/** Not a real booking: cancels its rooms here (the OTA is not told), and the
 * check leaves it alone from then on. */
export const ignoreOtaReservation = async (
  configId: number,
  otaReservationId: number
): Promise<ChannelManagerCallResult> => {
  const res = await apiFetch(
    `${base}/${configId}/reservations/${otaReservationId}/ignore`,
    { method: "POST" }
  );
  return res.json();
};

/** OTA bookings needing attention across the caller's own branches. */
// accountId narrows the count to the hotel being viewed: a super admin's token
// is not tied to one account, so without it the badge counted every hotel's.
export const getOtaAttention = async (accountId?: number): Promise<OtaAttention> => {
  const res = await apiFetch(
    accountId ? `/channel-manager/attention?accountId=${accountId}` : "/channel-manager/attention"
  );
  return res.json();
};

/** Fired after something on the Channel Manager page may have changed how
 * many OTA bookings need attention, so the menu badge updates at once
 * instead of at its next poll. */
export const OTA_ATTENTION_EVENT = "ich:ota-attention-changed";

export const notifyOtaAttentionChanged = () =>
  window.dispatchEvent(new Event(OTA_ATTENTION_EVENT));

/** A sample booking through the real webhook path: "book" | "modify" | "cancel". */
export const sendTestReservation = async (
  configId: number,
  action: "book" | "modify" | "cancel"
): Promise<OtaWebhookResult> => {
  const res = await apiFetch(`${base}/${configId}/test-reservation?action=${action}`, {
    method: "POST",
  });
  return res.json();
};

export const listChannelManagerConfigs = async (
  accountId: number
): Promise<ChannelManagerConfigDto[]> => {
  const res = await apiFetch(`${base}?accountId=${accountId}`);
  return res.json();
};

export const createChannelManagerConfig = async (
  payload: CreateChannelManagerConfigPayload
): Promise<ChannelManagerConfigDto> => {
  const res = await apiFetch(base, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updateChannelManagerConfig = async (
  configId: number,
  payload: UpdateChannelManagerConfigPayload
): Promise<ChannelManagerConfigDto> => {
  const res = await apiFetch(`${base}/${configId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const deleteChannelManagerConfig = async (
  configId: number
): Promise<void> => {
  await apiFetch(`${base}/${configId}`, { method: "DELETE" });
};

export const testChannelManagerConnection = async (
  configId: number
): Promise<ChannelManagerCallResult> => {
  const res = await apiFetch(`${base}/${configId}/test-connection`, {
    method: "POST",
  });
  return res.json();
};

export const getAiosellProperty = async (
  configId: number
): Promise<AiosellPropertyResult> => {
  const res = await apiFetch(`${base}/${configId}/aiosell-property`);
  return res.json();
};

export const getOtaMappings = async (configId: number): Promise<OtaMappings> => {
  const res = await apiFetch(`${base}/${configId}/mappings`);
  return res.json();
};

/** Sends Aiosell what differs now. `full` resends every night and rate. */
export const syncChannelManager = async (
  configId: number,
  full = false
): Promise<OtaSyncSummary> => {
  const res = await apiFetch(`${base}/${configId}/sync?full=${full}`, {
    method: "POST",
  });
  return res.json();
};

/** Reads what Aiosell holds and puts right whatever differs from ours. */
export const checkAgainstAiosell = async (configId: number): Promise<OtaCheckSummary> => {
  const res = await apiFetch(`${base}/${configId}/check`, { method: "POST" });
  return res.json();
};

export const getSyncLog = async (
  configId: number,
  take = 20
): Promise<OtaSyncLogEntry[]> => {
  const res = await apiFetch(`${base}/${configId}/sync-log?take=${take}`);
  return res.json();
};

export const getInventoryPreview = async (
  configId: number,
  nights = 14
): Promise<OtaInventoryPreview> => {
  const res = await apiFetch(`${base}/${configId}/inventory-preview?nights=${nights}`);
  return res.json();
};

/** Replaces the whole mapping: anything left out is unmapped. */
export const saveOtaMappings = async (
  configId: number,
  mappings: OtaMappings
): Promise<OtaMappings> => {
  const res = await apiFetch(`${base}/${configId}/mappings`, {
    method: "PUT",
    body: JSON.stringify(mappings),
  });
  return res.json();
};
